import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import {
  Box,
  VStack,
  HStack,
  Stack,
  toast,
  useColorModeValue,
} from "@chakra-ui/react";
import { Display03, Paragraph02 } from "~/components/Texts";
import Logo from "~/components/logo";
import { FiAlertTriangle } from "react-icons/fi";
import firebase from "~/firebase/clientApp";
import ShoppingBag from "~/components/animations/ShoppingBag";
import { useTheme } from "@chakra-ui/react";
import OrderDetailsPaymentStatusUpdate from "~/components/orderDetails/OrderDetailsPaymentStatusUpdate";
import OrderDetailsHeader from "~/components/orderDetails/OrderDetailsHeader";
import OrderDetailsShipping from "~/components/orderDetails/OrderDetailsShipping";
import OrderDetailsPayment from "~/components/orderDetails/OrderDetailsPayment";
import OrderDetailsBilling from "~/components/orderDetails/OrderDetailsBilling";
import OrderDetailsStatus from "~/components/orderDetails/OrderDetailsStatus";
import OrderDetailsProducts from "~/components/orderDetails/OrderDetailsProducts";
import OrderDetailsFooter from "~/components/orderDetails/OrderDetailsFooter";
import * as Sentry from "@sentry/nextjs";
import tinycolor from "tinycolor2";

const COLLECTION_SHOPS = "shops";
const COLLECTION_ORDERS = "orders";

export default function OrderDetails() {
  const router = useRouter();
  const [shopId, setShopId] = useState(router.query.shopId);
  const [orderId, setOrderId] = useState(router.query.orderId);
  const [shop, setShop] = useState();
  const [order, setOrder] = useState();
  const [loadingShop, setLoadingShop] = useState(true);
  const [loadingOrder, setLoadingOrder] = useState(true);

  useEffect(() => {
    if (router.query.shopId) setShopId(router.query.shopId);
  }, [router.query.shopId]);

  useEffect(() => {
    if (router.query.orderId) setOrderId(router.query.orderId);
  }, [router.query.orderId]);

  useEffect(() => {
    if (shopId) {
      const unsubscriber = firebase
        .firestore()
        .collection(COLLECTION_SHOPS)
        .doc(shopId)
        .onSnapshot(function (doc) {
          try {
            if (doc.exists) {
              let shop = { id: doc.id, ...doc.data() };
              setShop(shop);
              Sentry.setContext("shop", shop);
              Sentry.setTag("shopId", shop.id);
            }
          } catch (error) {
            Sentry.setContext("shop", null);
            Sentry.setTag("shopId", null);
            Sentry.captureException(error);
            toast({
              position: "top",
              title: "Ocurrió un error.",
              description: "No pudimos encontrar los datos de la tienda.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          } finally {
            setLoadingShop(false);
          }
        });

      // Unsubscribe shop listener on unmount
      return () => unsubscriber();
    }
  }, [shopId]);

  useEffect(() => {
    if (shopId && orderId) {
      const unsubscriber = firebase
        .firestore()
        .collection(COLLECTION_SHOPS)
        .doc(shopId)
        .collection(COLLECTION_ORDERS)
        .doc(orderId)
        .onSnapshot(function (doc) {
          try {
            if (doc.exists) {
              let order = { id: doc.id, ...doc.data() };
              setOrder(order);
              Sentry.setContext("order", order);
              Sentry.setTag("orderId", order.id);
            }
          } catch (error) {
            Sentry.setContext("order", null);
            Sentry.setTag("orderId", null);
            Sentry.captureException(error);
            toast({
              position: "top",
              title: "Ocurrió un error.",
              description: "No pudimos encontrar los datos de la orden.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          } finally {
            setLoadingOrder(false);
          }
        });

      // Unsubscribe order listener on unmount
      return () => unsubscriber();
    }
  }, [shopId, orderId]);

  if (shop && order)
    return (
      <VStack
        w="100%"
        minH="100%"
        p="4"
        backgroundColor={shop.theme.colors.background.strong}
      >
        <Box w="100%" maxW="960px">
          <OrderDetailsPaymentStatusUpdate shop={shop} />
        </Box>
        <VStack
          w="100%"
          h="100%"
          maxW="960px"
          borderRadius={shop.theme.radius}
          shadow="lg"
          px="8"
          py="6"
          spacing="10"
          backgroundColor={shop.theme.colors.background.superLite}
          color={tinycolor
            .mostReadable(
              shop.theme.colors.background.superLite,
              [shop.theme.colors.text],
              {
                includeFallbackColors: true,
              }
            )
            .toHexString()}
        >
          <OrderDetailsHeader shop={shop} order={order} />
          <OrderDetailsStatus order={order} />
          <Stack
            direction={{ base: "column", md: "row" }}
            w="100%"
            spacing="4"
            align="flex-start"
          >
            <OrderDetailsBilling shop={shop} order={order} />
            <OrderDetailsPayment shop={shop} order={order} />
          </Stack>
          <OrderDetailsShipping shop={shop} order={order} />
          <OrderDetailsProducts shop={shop} order={order} />
          <Box flex="1" />
          <OrderDetailsFooter shop={shop} orderId={orderId} />
        </VStack>
      </VStack>
    );

  if (loadingShop || loadingOrder) return <LoadingPlaceholder />;

  return <NoOrderFoundPlaceholder />;
}

function LoadingPlaceholder() {
  return (
    <VStack w="100%" h="100%" spacing="4" justify="center">
      <ShoppingBag />
      <Display03>Trayendo tu orden</Display03>
    </VStack>
  );
}

function NoOrderFoundPlaceholder() {
  const chakraTheme = useTheme();

  return (
    <VStack w="100%" h="100%" p="4" justify="center">
      <Box flex="1" />
      <VStack
        w="100%"
        maxW="960px"
        backgroundColor={useColorModeValue(
          "light.background.superLite",
          "dark.background.superLite"
        )}
        borderRadius="brand"
        overflow="hidden"
        shadow="lg"
      >
        <HStack
          w="100%"
          justify="center"
          p="4"
          backgroundColor="feel.warning"
          spacing="4"
        >
          <FiAlertTriangle
            size="36"
            color={useColorModeValue(
              chakraTheme.colors.light.brand.strong,
              chakraTheme.colors.dark.brand.strong
            )}
          />
          <Display03 textAlign="center">
            No pudimos encontrar tu orden
          </Display03>
        </HStack>
        <VStack w="100%" p="4">
          <ul style={{ width: "80%" }}>
            <li>
              <Paragraph02 py="2">
                Revisá que el link que usaste sea exactamente el mismo que te
                enviamos cuando realizaste tu compra.
              </Paragraph02>
            </li>
            <li>
              <Paragraph02 py="2">
                Si pagaste por tu compra a través de internet, es posible que
                cuando lo hayas hecho el pago haya sido rechazado y tu orden de
                compra no se haya generado.
              </Paragraph02>
            </li>
            <li>
              <Paragraph02 py="2">
                Si el problema persiste, ponete en contacto con la tienda en
                donde realizaste la compra.
              </Paragraph02>
            </li>
          </ul>
        </VStack>
      </VStack>
      <Box flex="1" />
      <Box p="4">
        <Logo />
      </Box>
    </VStack>
  );
}
